import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useInfiniteQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { flattenPages } from '@/api/pagination'
import { USER_STATUSES, type AdminUserListItem, type UserStatus } from '@/api/types'
import type { TranslationKey } from '@/i18n/keys'
import { usersListQuery, type UserFilters } from '@/features/users/users.queries'
import { formatBytes, formatDate, formatRelative } from '@/lib/format'
import { Avatar, Badge, Card, DataTable, PageHeader, SearchInput, type Column } from '@/ui'

const STATUS_LABELS: Record<UserStatus, TranslationKey> = {
  all: 'users.statusAll',
  active: 'users.statusActive',
  disabled: 'users.statusDisabled',
  deleted: 'users.statusDeleted',
}

const searchSchema = z.object({
  q: z.string().default(''),
  status: z.enum(USER_STATUSES).default('all'),
  roleId: z.string().optional(),
  sort: z.enum(['createdAt', 'displayName', 'lastSeenAt']).default('createdAt'),
  order: z.enum(['asc', 'desc']).default('desc'),
})

export const Route = createFileRoute('/_authed/users/')({
  validateSearch: searchSchema,
  component: UsersPage,
})

function UsersPage() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const search = Route.useSearch()
  const routeNavigate = Route.useNavigate()

  // The filters live in the URL, so a filtered view survives a reload and can be sent to
  // somebody else (best practices §7).
  const filters: UserFilters = {
    q: search.q,
    status: search.status,
    roleId: search.roleId,
    sort: search.sort,
    order: search.order,
  }

  const query = useInfiniteQuery(usersListQuery(filters))
  const { items, total } = flattenPages<AdminUserListItem>(query.data)

  const columns: Column<AdminUserListItem>[] = [
    {
      id: 'person',
      header: t('users.columnPerson'),
      sortKey: 'displayName',
      cell: (user) => (
        <div className="flex min-w-0 items-center gap-2.5">
          <Avatar src={user.avatarUrl} name={user.displayName} />
          <div className="min-w-0">
            <p className="truncate font-medium text-text">{user.displayName}</p>
            <p className="truncate text-xs text-text-secondary">{user.email}</p>
          </div>
        </div>
      ),
    },
    {
      id: 'status',
      header: t('users.columnStatus'),
      cell: (user) =>
        user.isDeleted ? (
          <Badge tone="danger" hasDot>
            {t('users.badgeDeleted')}
          </Badge>
        ) : user.isDisabled ? (
          <Badge tone="warning" hasDot>
            {t('users.badgeDisabled')}
          </Badge>
        ) : (
          <Badge tone="success" hasDot>
            {t('users.badgeActive')}
          </Badge>
        ),
    },
    {
      id: 'groups',
      header: t('users.columnGroups'),
      isSecondary: true,
      cell: (user) =>
        user.roleNames.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {user.roleNames.slice(0, 2).map((name) => (
              <Badge key={name}>{name}</Badge>
            ))}
            {user.roleNames.length > 2 ? <Badge>+{user.roleNames.length - 2}</Badge> : null}
          </div>
        ) : (
          <span className="text-text-muted">{t('common.notSet')}</span>
        ),
    },
    {
      id: 'worlds',
      header: t('users.columnWorlds'),
      align: 'end',
      isSecondary: true,
      cell: (user) => <span data-numeric>{user.worldCount}</span>,
    },
    {
      id: 'storage',
      header: t('users.columnStorage'),
      align: 'end',
      isSecondary: true,
      cell: (user) => (
        <span data-numeric className="text-text-secondary">
          {formatBytes(user.storageBytes, i18n.language)}
        </span>
      ),
    },
    {
      id: 'lastSeen',
      header: t('users.columnLastSeen'),
      sortKey: 'lastSeenAt',
      align: 'end',
      cell: (user) => (
        <span className="text-text-secondary">
          {user.lastSeenAt ? formatRelative(user.lastSeenAt, i18n.language) : t('common.never')}
        </span>
      ),
    },
    {
      id: 'joined',
      header: t('users.columnJoined'),
      sortKey: 'createdAt',
      align: 'end',
      isSecondary: true,
      cell: (user) => (
        <span className="text-text-secondary">{formatDate(user.createdAt, i18n.language)}</span>
      ),
    },
  ]

  return (
    <>
      <PageHeader
        title={t('users.title')}
        description={t('users.subtitle')}
        actions={
          total > 0 ? (
            <span data-numeric className="text-[0.8125rem] text-text-secondary">
              {t('common.showing', { count: items.length, total })}
            </span>
          ) : undefined
        }
      />

      <Card isFlush>
        <div className="flex flex-wrap items-center gap-2 border-b border-border p-3">
          <SearchInput
            value={search.q}
            onChange={(q) => {
              void routeNavigate({ search: (prev) => ({ ...prev, q }) })
            }}
            placeholder={t('users.searchPlaceholder')}
            className="min-w-56 flex-1"
          />

          <label className="sr-only" htmlFor="status-filter">
            {t('users.status')}
          </label>
          <select
            id="status-filter"
            value={search.status}
            onChange={(event) => {
              void routeNavigate({
                search: (prev) => ({
                  ...prev,
                  status: event.target.value as (typeof USER_STATUSES)[number],
                }),
              })
            }}
            className="h-9 rounded-control border border-border-strong/60 bg-surface px-2.5 text-[0.8125rem] text-text"
          >
            {USER_STATUSES.map((status) => (
              <option key={status} value={status}>
                {t(STATUS_LABELS[status])}
              </option>
            ))}
          </select>
        </div>

        <DataTable
          columns={columns}
          rows={items}
          getRowId={(user) => user.id}
          caption={t('users.title')}
          isLoading={query.isPending}
          error={query.error}
          onRetry={() => {
            void query.refetch()
          }}
          sort={{ key: search.sort, order: search.order }}
          onSortChange={(next) => {
            void routeNavigate({
              search: (prev) => ({
                ...prev,
                sort: next.key as typeof search.sort,
                order: next.order,
              }),
            })
          }}
          onRowClick={(user) => {
            void navigate({ to: '/users/$userId', params: { userId: user.id } })
          }}
          emptyTitle={search.q || search.status !== 'all' ? t('state.emptyFiltered') : t('users.empty')}
          emptyDescription={
            search.q || search.status !== 'all' ? t('state.emptyFilteredHint') : undefined
          }
          hasNextPage={query.hasNextPage}
          isFetchingNextPage={query.isFetchingNextPage}
          onLoadMore={() => {
            void query.fetchNextPage()
          }}
        />
      </Card>
    </>
  )
}
